import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { translations, flags, languageNames } from '../data/translations';

const { FiMusic, FiCpu, FiArrowRight, FiGlobe, FiSearch, FiCode, FiCheckCircle, FiChevronDown } = FiIcons;

const HomePage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('sunosync_lang') || 'en');
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Click away listener for language dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem('sunosync_lang', newLang);
    setIsLangMenuOpen(false);
  };

  const t = translations[lang] || translations['en'];

  return (
    <div className="min-h-screen bg-stone-50 font-sans selection:bg-orange-200 selection:text-orange-900 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-stone-200 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiMusic} className="text-2xl text-orange-600" />
            <span className="font-extrabold text-xl tracking-tight text-stone-800">Suno<span className="text-orange-600">Sync</span></span>
          </div>
          
          <div className="flex items-center gap-3 md:gap-6">
            {/* Language Switcher */}
            <div className="relative" ref={menuRef}>
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-stone-100 transition-colors text-stone-700 font-medium text-sm border border-transparent hover:border-stone-200"
              >
                <span className="text-lg">{flags[lang]}</span>
                <span className="hidden sm:inline uppercase">{lang}</span>
                <SafeIcon icon={FiChevronDown} className={`transition-transform duration-200 ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-stone-100 overflow-hidden py-1 z-50"
                  >
                    {Object.keys(flags).map((lKey) => (
                      <button 
                        key={lKey}
                        onClick={() => handleLanguageChange(lKey)}
                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${lang === lKey ? 'bg-orange-50 text-orange-700 font-bold' : 'text-stone-600 hover:bg-stone-50'}`}
                      >
                        <span className="text-lg">{flags[lKey]}</span>
                        {languageNames[lKey]}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button 
              onClick={() => navigate('/app')}
              className="px-4 py-2 md:px-5 md:py-2 bg-stone-900 text-white rounded-xl font-bold hover:bg-stone-800 transition-all flex items-center gap-2 text-sm whitespace-nowrap"
            >
              {t.nav.app} <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-orange-100/50 to-transparent pointer-events-none" />
        <div className="container mx-auto text-center relative z-10 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm mb-6 border border-orange-200"
          >
            <SafeIcon icon={FiCpu} /> {t.hero.badge}
          </motion.div>
          <motion.h1 
            key={`hero-title-${lang}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold text-stone-900 tracking-tight mb-6 leading-tight"
          >
            {t.hero.title1} <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-500">{t.hero.titleHighlight}</span> {t.hero.title2}
          </motion.h1>
          <motion.p 
            key={`hero-sub-${lang}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-stone-600 mb-10 max-w-2xl mx-auto"
          >
            {t.hero.subtitle}
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <button 
              onClick={() => navigate('/app')}
              className="px-8 py-4 bg-orange-600 text-white rounded-2xl font-bold text-lg hover:bg-orange-700 hover:scale-105 active:scale-95 transition-all shadow-xl shadow-orange-200 flex items-center gap-3 mx-auto"
            >
              {t.hero.cta} <SafeIcon icon={FiArrowRight} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Features - GEO/SEO Optimized */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">{t.features.title}</h2>
            <p className="text-stone-500 max-w-2xl mx-auto">{t.features.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: FiSearch, title: t.features.items[0].title, desc: t.features.items[0].desc },
              { icon: FiCode, title: t.features.items[1].title, desc: t.features.items[1].desc },
              { icon: FiGlobe, title: t.features.items[2].title, desc: t.features.items[2].desc }
            ].map((feature, idx) => (
              <motion.div 
                key={`${lang}-feat-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-3xl bg-stone-50 border border-stone-100 hover:border-orange-200 hover:shadow-lg transition-all group"
              >
                <div className="w-14 h-14 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={feature.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-3">{feature.title}</h3>
                <p className="text-stone-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AEO / FAQ Section */}
      <section className="py-20 bg-stone-900 text-stone-300">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.faq.title}</h2>
            <p className="text-stone-400">{t.faq.subtitle}</p>
          </div>

          <div className="space-y-6">
            {t.faq.items.map((item, idx) => (
              <div key={`${lang}-faq-${idx}`} className="bg-stone-800/50 p-6 rounded-2xl border border-stone-700">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-orange-500" /> 
                  {item.q}
                </h3>
                <p className="leading-relaxed" dangerouslySetInnerHTML={{ __html: item.a }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-stone-500 border-t border-stone-200">
        <p>© {new Date().getFullYear()} {t.footer}</p>
      </footer>
    </div>
  );
};

export default HomePage;